import { LoadingButton } from "@mui/lab";
import { Box, FormHelperText, Grid, Link, Typography } from "@mui/material";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import { StripeCustomer } from "../schema";

interface Props {
  handleConfirm: () => Promise<any>;
  submitAddressLoading: boolean;
  returnUrl: string;
  setUpClientSecret?: string;
  updateCustomer: (e: CustomEvent, paymentMethodId?: string) => Promise<any>;
}

const PaymentForm = ({
  handleConfirm,
  updateCustomer,
  submitAddressLoading,
  returnUrl,
  setUpClientSecret,
}: Props) => {
  const { customer, setCustomer, noNeedsToPay, requiresBillingAddress } =
    useContext(CheckoutContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const stripe = useStripe();
  const elements = useElements();
  const { setErrorAlert } = useContext(AlertContext);

  const handleSubmit = async (event: CustomEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !setUpClientSecret) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(
          submitError.message ||
            "Something went wrong. Please refresh and try again.",
        );
        return;
      }
      setLoading(true);

      const { error, setupIntent } = await stripe.confirmSetup({
        //`Elements` instance that was used to create the Payment Element
        elements,
        clientSecret: setUpClientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              address: {
                country: customer?.address?.country,
                postal_code: customer?.address?.postal_code,
              },
            },
          },
        },
      });
      if (error) {
        setError(
          error.message ||
            "Something went wrong. Please refresh and try again.",
        );
        return;
      }

      if (setupIntent?.status === "succeeded" && setupIntent?.payment_method) {
        await updateCustomer(event, setupIntent.payment_method as string);
        await handleConfirm();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: CustomEvent) => {
    if (e.value.address?.line1 && error === "Please fill out address.") {
      setError("");
    } else if (
      e.value.address?.country &&
      error === "Please fill out country."
    ) {
      setError("");
    } else if (
      e.value.address?.postal_code &&
      error === "Please fill out postal code."
    ) {
      setError("");
    }
    if (e.value.address) {
      setCustomer((prev: StripeCustomer) => ({
        ...prev,
        address: e.value.address,
      }));
    }
  };

  const handleSubmitAddress = (e: CustomEvent) => {
    e.preventDefault();

    if (!customer.address?.line1) {
      setError("Please fill out address.");
      return;
    }

    if (!customer.address?.country) {
      setError("Please fill out country.");
      return;
    }

    updateCustomer(e);
  };

  const onSubmit = (e: CustomEvent) => {
    e.preventDefault();
    if (setUpClientSecret) {
      handleSubmit(e);
    }
  };

  const renderBody = () => {
    if (requiresBillingAddress) {
      return (
        <form id="customer-form" onSubmit={handleSubmitAddress}>
          <Box sx={{ mb: 2 }}>
            <AddressElement
              options={{
                mode: "billing",
                autocomplete: {
                  mode: "google_maps_api",
                  apiKey: process.env.REACT_APP_GOOGLE_MAPS_JS_KEY || "",
                },
                defaultValues: {
                  name: customer?.name,
                  address: {
                    country: customer?.address?.country,
                    postal_code: customer?.address?.postal_code,
                  },
                },
              }}
              onChange={handleChange}
            />

            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ my: 2 }}
              disableElevation
              loading={submitAddressLoading}
            >
              Next
            </LoadingButton>
          </Box>
        </form>
      );
    } else {
      const options: any = {
        layout: {
          type: "tabs",
          defaultCollapsed: false,
        },
        fields: {
          billingDetails: {
            address: { country: "never", postalCode: "never" },
          },
        },
        terms: {
          card: "never",
          applePay: "never",
          googlePay: "never",
        },
      };

      return (
        <form id="payment-form" onSubmit={onSubmit}>
          {noNeedsToPay && (
            <>
              We will not charge your card at this time. Your invoice will be
              deducted from your current credit balance. However, we will use
              this payment method for future payments.
            </>
          )}
          <PaymentElement options={options} onChange={handleChange} />

          <Grid container>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ my: 2 }}
              disableElevation
              loading={loading}
            >
              Confirm
            </LoadingButton>
          </Grid>

          <FormHelperText>
            By confirming your subscription, you allow Bento to charge you for
            future payments in accordance with our{" "}
            <Link
              href="https://www.onbento.com/terms-of-services"
              target="_blank"
            >
              <strong>Terms of Service</strong>
            </Link>
            . You can always cancel your subscription.
          </FormHelperText>
        </form>
      );
    }
  };

  return (
    <>
      {" "}
      <Typography variant="h4" sx={{ mb: 2 }}>
        Checkout
      </Typography>
      {renderBody()}
      {error && <Typography color="error">{error}</Typography>}
    </>
  );
};

export default PaymentForm;
